// @flow
import React, {useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
} from '@material-ui/core';

import {colors} from '../../../../assets/styles/colors';
import EditVariantInput from '../../../products/components/ProductVariants/EditVariantModal/EditVariantInput';
import {RadioButtonChecked, RadioButtonUnchecked} from '@material-ui/icons';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {
  getProductVariantOptionService,
  saveProductVariantOptionService,
  updateProductVariantOptionService,
} from '../../services';
import SnackBar from '../../../../elements/SnackBar/SnackBar';
import {uploadImageService} from '../../../file-storage/promises';
import {useBindedActions} from '../../../../utils/globalStore';

import colorVariantImg from '../../../../assets/img/product-variants/color-variant.png';
import textVariantImg from '../../../../assets/img/product-variants/text-variant.png';
import imageVariantImg from '../../../../assets/img/product-variants/image-variant.png';

import {useForm} from 'react-hook-form';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import EditVariantValue from './EditVariantValue';
import {useAlert} from '../../../../elements/Alert/AlertContext';
import {createFile} from '../../../../utils/image';
import {getImageUrl} from '../../../../utils/image_provider';

import {actions as ProductActions} from '../../../products/duck';
import {arrayMove} from '../../../../utils/array';

import type {
  ProductVariantOption,
  ProductVariantOptionValue,
  ProductVariantOptionImageShape,
  ProductVariantOptionDisplayType,
} from '../../../../entities';

import type {Theme} from '@material-ui/core';
import SortableList, {SortableItem} from 'react-easy-sort';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginTop: '1rem',
  },
  bg: {
    background: '#FFFFFF',
    padding: theme.spacing(3),
  },
  title: {
    textAlign: 'center',
    fontWeight: 600,
    fontSize: '24px',
    marginTop: theme.spacing(2),
  },
  subtitle: {
    textAlign: 'center',
    fontSize: '14px',
    marginBottom: theme.spacing(2),
  },
  text: {
    fontSize: 14,
    color: colors.grey900,
  },
  step: {
    color: colors.grey800,
    fontSize: 14,
    fontWeight: 600,
  },
  stepContent: {
    marginTop: theme.spacing(2),
  },
  radioGroup: {
    gap: '8px',
    flexDirection: 'row',
  },
  customButton: {
    display: 'flex',
    width: '100%',
    gap: '8px',
    padding: theme.spacing(1),
    borderRadius: '10px',
    flexDirection: 'column',
    '& img': {
      maxWidth: '60%',
      width: '60%',
      [theme.breakpoints.up('md')]: {
        maxWidth: '100%',
        width: '100%',
      },
    },
  },
  variantTypeBtn: {
    border: '1px solid transparent',
    maxWidth: '100%',
  },
  selected: {
    border: '1px solid #7B39FF',
  },
  btnText: {
    textTransform: 'none',
  },
  wrapVariants: {
    position: 'relative',
    padding: theme.spacing(1),
    borderRadius: '8px',
    border: `1px solid ${colors.grey300}`,
    marginBottom: theme.spacing(1),
  },
  divider: {
    width: '100%',
    margin: theme.spacing(2, 0),
    background: colors.grey300,
  },

  editBtn: {
    position: 'absolute',
    top: 8,
    right: 8,
  },
}));

export type OptionValue = ProductVariantOptionValue & {
  file?: File,
  preview?: string,
};

type Status = 'ERROR' | 'LOADING' | 'SAVING';

type Props = {
  variantOptionId?: string | null,
};

const DISPLAY_TYPE_MAP = {
  '0': 'TEXT',
  '1': 'IMAGE',
  '2': 'COLOR',
};

const IMAGE_VARIANT_MAP = {
  TEXT: textVariantImg,
  IMAGE: imageVariantImg,
  COLOR: colorVariantImg,
};

const checkedCheckbox = (
  <RadioButtonChecked color="primary" style={{fontSize: '1.75rem'}} />
);
const uncheckedCheckbox = (
  <RadioButtonUnchecked fontSize="large" style={{fontSize: '1.75rem'}} />
);

export default function EditVariantForm(props: Props) {
  const {variantOptionId = 'new'} = props;

  const classes = useStyles();
  const history = useHistory();

  const {showAlert} = useAlert();

  const {register, errors, setValue, handleSubmit} = useForm();

  const [error, setError] = useState<string>('');
  const {fetchProductVariantOptions} = useBindedActions(ProductActions);

  const [status, setStatus] = useState<Status | null>(null);
  const [optionValues, setOptionValues] = useState<OptionValue[]>([]);

  const [imageShape, setImageShape] = useState<ProductVariantOptionImageShape>(
    'CIRCULAR'
  );

  const [
    displayType,
    setDisplayType,
  ] = useState<ProductVariantOptionDisplayType>('TEXT');

  const onVariantSave = (v: any) => {
    setOptionValues(prev => [...prev, v]);
  };

  const getButtonText = () => {
    if (status === 'LOADING') return 'Subiendo imágenes...';
    if (status === 'SAVING') return 'Guardando variante...';
    return 'Guardar cambios';
  };

  // const isValidOptionValues = () => {
  //   if (displayType === 'IMAGE') {
  //     const valid =
  //       optionValues.filter(el => !el.file && !el.imageId).length === 0;
  //     if (!valid)
  //       setError('Todas las opciones de variante deben tener una imagen');
  //     return valid;
  //   } else if (displayType === 'COLOR') {
  //     const valid = !optionValues.some(el => !el.color);
  //     if (!valid)
  //       setError('Todas las opciones de variante deben tener un color');
  //     return valid;
  //   }

  //   return true;
  // };

  const validateRepeatedVariantValues = () => {
    let isRepeated = false;

    const draftNameMap = {};

    for (const value of optionValues) {
      const name = value.id ? value.label?.trim() : value.name?.trim();

      if (draftNameMap[name]) {
        isRepeated = true;
      } else {
        draftNameMap[name] = true;
      }

      if (isRepeated) break;
    }

    if (isRepeated)
      showAlert('Las opciones de variante deben tener diferentes nombres', {
        type: 'info',
      });
    return isRepeated;
  };

  const validateImageShapeVariantValues = () => {
    const imagesSaved = [];

    const imagesToUpload = [];

    optionValues.forEach((o, idx) => {
      if (o.file && !o.file.name.startsWith('DEFAULT_IMAGE')) {
        imagesSaved.push(o);
      }

      if (o.imageId) imagesToUpload.push(o);
    });

    if (imagesSaved.length + imagesToUpload.length === optionValues.length)
      return true;
    else {
      showAlert(
        'Si la variante se mostrará como foto, todas las opciones deben tener una imagen',
        {type: 'info', clearPreviousAlerts: true}
      );
      return false;
    }
  };

  const validateColorVariantValues = () => {
    let isCorrect = optionValues.every(v => v.color);

    if (isCorrect) return true;
    else {
      showAlert(
        'Si la variante se mostrará como color, todas las opciones deben tener un color',
        {type: 'info', clearPreviousAlerts: true}
      );
      return false;
    }
  };

  const onSortEnd = (oldIndex: number, newIndex: number) => {
    setOptionValues(array => arrayMove(optionValues, oldIndex, newIndex));
  };

  const onSubmit = async ({name}: {name: string}) => {
    setError('');

    // Validaciones previas
    const isRepeatedValues = validateRepeatedVariantValues();
    if (isRepeatedValues) return;

    const isValidForImageDisplay =
      displayType === 'IMAGE' ? validateImageShapeVariantValues() : true;

    const isValidForColorDisplay =
      displayType === 'COLOR' ? validateColorVariantValues() : true;

    if (!isValidForImageDisplay) return;
    if (!isValidForColorDisplay) return;

    // Mapear archivos que necesitan subirse con su índice
    const filesToUploadMap: {[key: number]: File} = {};
    optionValues.forEach((o, idx) => {
      if (o.file && !o.file.name.startsWith('DEFAULT_IMAGE')) {
        filesToUploadMap[idx] = o.file;
      }
    });

    // Preparar valores base
    const values = optionValues.map((op, index) => {
      const {preview, file, imageId, ...rest} = op;
      const newObj: {[key: string]: any} = {
        name: rest.id ? rest.label : rest.name,
        imageId: imageId ? imageId : undefined,
        color: rest.color ?? undefined,
        number: index,
      };
      if (rest.id) {
        newObj.productVariantOptionValueId = rest.id;
      }
      return newObj;
    });

    // Subir imágenes una por una
    const filesToUploadIndices = Object.keys(filesToUploadMap);
    if (filesToUploadIndices.length > 0) {
      setStatus('LOADING');

      const uploadResults = {
        successful: 0,
        failed: 0,
        errors: [],
      };

      for (const indexStr of filesToUploadIndices) {
        const index = Number(indexStr);
        const file = filesToUploadMap[index];
        const variantName = values[index].name;

        try {
          const currentImageNumber =
            uploadResults.successful + uploadResults.failed + 1;
          showAlert(
            `Subiendo imagen ${currentImageNumber}/${filesToUploadIndices.length} (${variantName})...`,
            {type: 'info', clearPreviousAlerts: true}
          );

          const imagesArr = await uploadImageService(
            [file],
            'im/product-variant-value'
          );

          if (imagesArr && imagesArr.length > 0) {
            values[index].imageId = imagesArr[0].id;
            uploadResults.successful++;
          } else {
            uploadResults.failed++;
            uploadResults.errors.push(
              `${variantName}: No se recibió ID de imagen`
            );
          }
        } catch (err) {
          uploadResults.failed++;
          const errorMsg = err?.message || 'Error desconocido';
          uploadResults.errors.push(`${variantName}: ${errorMsg}`);
          console.error(`Error subiendo imagen para "${variantName}":`, err);
        }
      }

      setStatus(null);

      // Mostrar resumen de carga
      if (uploadResults.failed > 0) {
        const errorDetails = uploadResults.errors.join('\n');
        showAlert(
          `Se subieron ${uploadResults.successful} de ${filesToUploadIndices.length} imágenes. ${uploadResults.failed} fallaron:\n${errorDetails}`,
          {type: 'warning', clearPreviousAlerts: true}
        );

        // Si todas fallaron, detener el proceso
        if (uploadResults.successful === 0) {
          setError(
            'No se pudo subir ninguna imagen. Verifica el tamaño (máx 1MB) e intenta nuevamente.'
          );
          return;
        }
      } else {
        showAlert(
          `Todas las imágenes (${uploadResults.successful}) se subieron correctamente`,
          {type: 'success', clearPreviousAlerts: true}
        );
      }
    }

    // Preparar request final
    const request = {
      name,
      values,
      displayType,
      imageShape,
    };

    setStatus('SAVING');

    try {
      if (variantOptionId !== 'new') {
        const result = await updateProductVariantOptionService(
          request,
          variantOptionId
        );

        setStatus(null);
        if (result) {
          showAlert('Variante actualizada correctamente', {
            type: 'success',
            clearPreviousAlerts: true,
          });
          fetchProductVariantOptions();
          history.push('/products-variants');
        }
      } else {
        const result = await saveProductVariantOptionService(request);

        setStatus(null);
        if (result) {
          showAlert('Variante guardada correctamente', {
            type: 'success',
            clearPreviousAlerts: true,
          });
          fetchProductVariantOptions();
          history.push('/products-variants');
        }
      }
    } catch (err) {
      setStatus('ERROR');
      const errorMsg = err?.message || 'Error al guardar la variante';
      setError(errorMsg);
      showAlert(`Error: ${errorMsg}`, {
        type: 'error',
        clearPreviousAlerts: true,
      });
      console.error('Error guardando variante:', err);
    }
  };

  useEffect(() => {
    if (variantOptionId && variantOptionId !== 'new') {
      getProductVariantOptionService(variantOptionId)
        .then((el: ProductVariantOption) => {
          if (!el) return;

          setValue('name', el.label);
          setImageShape(el.imageShape);
          setDisplayType(el.displayType);

          fillOptionValuesWithFile(el.values);
        })
        .catch(err => console.log(err));
    }
  }, [setValue, variantOptionId]);

  const fillOptionValuesWithFile = async (
    values: ProductVariantOptionValue[]
  ) => {
    const length = values.length;
    const draft = [];
    for (let i = 0; i < length; i++) {
      let item = values[i];

      if (item.imageId) {
        try {
          const {file} = await createFile(
            getImageUrl(item.imageId),
            'DEFAULT_IMAGE'
          );
          item = {...item, file};
        } catch (err) {
          item = {...item, file: new File([], 'DEFAULT_IMAGE.jpg')};
        }
      }

      draft[i] = item;
    }
    // $FlowFixMe
    setOptionValues(draft);
  };

  // const handleEnableProductValue = (e, i: number) => {
  //   e.stopPropagation();
  //   const aux = [...optionValues];
  //   setOptionValues(aux);
  // };

  // const handleUpdate = (vUpdated: ProductVariantOption) => {
  //   const optValues = vUpdated.values.map(el => ({
  //     ...el,
  //     enabled: !!enabledValues.find(s => s.id === el.id),
  //   }));
  //   setOptionValues(optValues);
  // };

  const handleUpdateValue = (newValue: OptionValue, idx) => {
    const aux = [...optionValues];
    aux[idx] = newValue;
    setOptionValues(aux);
  };

  const handleDeleteOptionValue = (idx: number) => {
    const aux = [...optionValues];
    aux.splice(idx, 1);
    setOptionValues(aux);
  };

  return (
    <>
      <SnackBar
        mode="error"
        message={status === 'ERROR' ? 'Error al editar la variante.' : ''}
      />

      <SnackBar
        mode="info"
        message={error ?? ''}
        handleClose={() => setError('')}
      />
      {/* {openConfirmModal && (
        <ConfirmVariantEditModal
          variantOptionId={variantOption.id}
          imageShape={variantOption.imageShape}
          displayType={displayType}
          optionValues={optionValues}
          handleClose={() => setOpenConfirmModal(false)}
          onSaveSuccess={handleUpdate}
        />
      )} */}
      <div className={classes.root}>
        <Grid container>
          <Grid item xs={12} xl={8} className={classes.bg}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography className={classes.step}>
                  1. Nombre de la variante
                </Typography>
                {/* <Hidden mdUp>
                  <Typography className={classes.text}>
                    Solo se podrá editar las variantes desde una computadora.
                  </Typography>
                </Hidden> */}

                <div className={classes.stepContent}>
                  <CustomTextField
                    label="Nombre de la variante"
                    name="name"
                    className={classes.textField}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.name}
                    helperText={errors.name ? errors.name.message : ''}
                    inputRef={register({
                      required: true,
                    })}
                    type="text"
                  />
                </div>
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.step}>
                  2. Define como se mostrará esta variante (Referencial)
                </Typography>

                <div className={classes.stepContent}>
                  <Typography gutterBottom className={classes.text}>
                    Solo se mostrará con foto si es que la variante tiene una.{' '}
                  </Typography>

                  <Grid container spacing={1} style={{marginTop: '1rem'}}>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth
                        className={`${classes.variantTypeBtn} ${
                          displayType === DISPLAY_TYPE_MAP[0]
                            ? classes.selected
                            : ''
                        }`}
                        onClick={() => setDisplayType(DISPLAY_TYPE_MAP[0])}
                        aria-label="Seleccionar variante de tipo texto"
                      >
                        <div className={classes.customButton}>
                          <div
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'flex-start',
                              gap: '8px',
                            }}
                          >
                            {displayType === DISPLAY_TYPE_MAP[0]
                              ? checkedCheckbox
                              : uncheckedCheckbox}
                            <Typography className={classes.btnText}>
                              Texto
                            </Typography>
                          </div>
                          <div style={{display: 'flex', width: '120px'}}>
                            <img
                              src={IMAGE_VARIANT_MAP.TEXT}
                              alt="normal variant"
                            />
                          </div>
                        </div>
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth
                        className={`${classes.variantTypeBtn} ${
                          displayType === DISPLAY_TYPE_MAP[1]
                            ? classes.selected
                            : ''
                        }`}
                        onClick={() => setDisplayType(DISPLAY_TYPE_MAP[1])}
                        aria-label="Seleccionar variante de tipo imagen"
                      >
                        <div className={classes.customButton}>
                          <div
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'flex-start',
                              gap: '8px',
                            }}
                          >
                            {displayType === DISPLAY_TYPE_MAP[1]
                              ? checkedCheckbox
                              : uncheckedCheckbox}
                            <Typography className={classes.btnText}>
                              Foto
                            </Typography>
                          </div>

                          <div style={{display: 'flex', width: '120px'}}>
                            <img
                              src={IMAGE_VARIANT_MAP.IMAGE}
                              alt="new variant"
                            />
                          </div>
                        </div>
                      </Button>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        fullWidth
                        className={`${classes.variantTypeBtn} ${
                          displayType === DISPLAY_TYPE_MAP[2]
                            ? classes.selected
                            : ''
                        }`}
                        onClick={() => setDisplayType(DISPLAY_TYPE_MAP[2])}
                        aria-label="Seleccionar variante de tipo color"
                      >
                        <div className={classes.customButton}>
                          <div
                            style={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'flex-start',
                              gap: '8px',
                            }}
                          >
                            {displayType === DISPLAY_TYPE_MAP[2]
                              ? checkedCheckbox
                              : uncheckedCheckbox}
                            <Typography className={classes.btnText}>
                              Color
                            </Typography>
                          </div>

                          <div style={{display: 'flex', width: '120px'}}>
                            <img
                              src={IMAGE_VARIANT_MAP.COLOR}
                              alt="new variant"
                            />
                          </div>
                        </div>
                      </Button>
                    </Grid>
                  </Grid>
                </div>
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.step}>
                  3. Define la forma
                </Typography>
                <div className={classes.stepContent}>
                  <RadioGroup
                    name="shape"
                    value={imageShape}
                    onChange={e => setImageShape(e.target.value)}
                    classes={{
                      root: classes.radioGroup,
                    }}
                  >
                    <FormControlLabel
                      value="CIRCULAR"
                      control={
                        <Radio
                          color="primary"
                          className={classes.deliveryRadioIcon}
                        />
                      }
                      label="Redonda"
                    />
                    <FormControlLabel
                      value="RECTANGULAR"
                      control={
                        <Radio
                          color="primary"
                          className={classes.deliveryRadioIcon}
                        />
                      }
                      label="Cuadrada"
                    />
                  </RadioGroup>
                </div>
              </Grid>

              <Grid item xs={12}>
                <Typography className={classes.step}>
                  4. Opciones creadas
                </Typography>
                {displayType === 'IMAGE' && (
                  <Typography
                    className={classes.text}
                    style={{
                      marginTop: '8px',
                      color: colors.grey700,
                      fontSize: '13px',
                    }}
                  >
                    <span role="img" aria-label="información">
                      💡
                    </span>{' '}
                    Las imágenes deben pesar menos de 1MB. Si una imagen es muy
                    grande, comprímela antes de subirla.
                  </Typography>
                )}
                <div className={classes.stepContent}>
                  <SortableList onSortEnd={onSortEnd}>
                    {optionValues.length > 0 && (
                      <Grid
                        container
                        className={classes.wrapVariants}
                        spacing={1}
                      >
                        {optionValues.map((v, idx: number) => {
                          return (
                            <SortableItem key={v.id}>
                              <Grid item xs={12} md={6} key={idx}>
                                <EditVariantValue
                                  value={v}
                                  displayType={displayType}
                                  imageShape={imageShape}
                                  order={idx}
                                  onUpdateValue={v => handleUpdateValue(v, idx)}
                                  onDeleteValue={() =>
                                    handleDeleteOptionValue(idx)
                                  }
                                />
                              </Grid>
                            </SortableItem>
                          );
                        })}
                      </Grid>
                    )}
                  </SortableList>
                  <EditVariantInput
                    onVariantSave={onVariantSave}
                    displayType={displayType}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <CustomButton
                  variant="contained"
                  onClick={handleSubmit(onSubmit)}
                  disabled={
                    ['SAVING', 'LOADING'].includes(status) ||
                    optionValues.length === 0
                  }
                >
                  {getButtonText()}
                </CustomButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    </>
  );
}
